import React, { ReactNode, useEffect, useState } from 'react';
import {
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, ChevronsUpDown, Loader2 } from 'lucide-react';

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData, TValue> {
    align?: 'left' | 'right';
    className?: string;
  }
}

interface DataTableProps<T> {
  data: T[];
  columns: ColumnDef<T, any>[];
  getRowId: (row: T) => string;
  loading?: boolean;
  toolbar?: ReactNode;
  emptyState?: ReactNode;
  // Free-text search: rows match when getSearchText(row) contains the query.
  globalFilter?: string;
  getSearchText?: (row: T) => string;
  // Controlled by the caller's toolbar; each id must match a column with a filterFn.
  columnFilters?: ColumnFiltersState;
  initialSorting?: SortingState;
  pageSizes?: number[];
}

export function DataTable<T>({
  data,
  columns,
  getRowId,
  loading = false,
  toolbar,
  emptyState,
  globalFilter = '',
  getSearchText,
  columnFilters = [],
  initialSorting = [],
  pageSizes = [25, 50, 100],
}: DataTableProps<T>) {
  const [sorting, setSorting] = useState<SortingState>(initialSorting);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: pageSizes[0] });

  const searchFilter: FilterFn<T> = (row, _columnId, query: string) =>
    !getSearchText || getSearchText(row.original).toLowerCase().includes(query.toLowerCase());

  const table = useReactTable({
    data,
    columns,
    getRowId,
    state: { sorting, pagination, columnFilters, globalFilter },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    globalFilterFn: searchFilter,
    getColumnCanGlobalFilter: () => true,
    // Reset by hand on filter change only, so a refetch after a row action keeps the page.
    autoResetPageIndex: false,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const filterKey = JSON.stringify([globalFilter, columnFilters]);
  useEffect(() => {
    setPagination(p => ({ ...p, pageIndex: 0 }));
  }, [filterKey]);

  const total = table.getFilteredRowModel().rows.length;
  const pageCount = table.getPageCount();
  useEffect(() => {
    if (pagination.pageIndex > 0 && pagination.pageIndex >= pageCount) {
      setPagination(p => ({ ...p, pageIndex: Math.max(pageCount - 1, 0) }));
    }
  }, [pageCount, pagination.pageIndex]);

  const first = total === 0 ? 0 : pagination.pageIndex * pagination.pageSize + 1;
  const last = Math.min(total, (pagination.pageIndex + 1) * pagination.pageSize);

  return (
    <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
      {toolbar && (
        <div className="p-4 border-b border-[#e2e8f0]">
          <div className="flex flex-wrap gap-3 items-center">{toolbar}</div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center py-20 text-slate-400">
          <Loader2 size={24} className="animate-spin" />
        </div>
      ) : total === 0 ? (
        emptyState ?? <div className="text-center py-16 text-sm text-slate-400">No results</div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                {table.getHeaderGroups().map(group => (
                  <tr key={group.id} className="border-b border-[#e2e8f0] bg-slate-50/50">
                    {group.headers.map(header => {
                      const meta = header.column.columnDef.meta;
                      const sorted = header.column.getIsSorted();
                      const canSort = header.column.getCanSort();
                      const label = flexRender(header.column.columnDef.header, header.getContext());
                      return (
                        <th key={header.id}
                          className={`px-5 py-3 text-xs font-bold uppercase tracking-wider text-slate-400 ${meta?.align === 'right' ? 'text-right' : 'text-left'}`}>
                          {canSort ? (
                            <button type="button" onClick={header.column.getToggleSortingHandler()}
                              className={`inline-flex items-center gap-1 uppercase tracking-wider hover:text-navy transition-colors ${sorted ? 'text-navy' : ''}`}>
                              {label}
                              {sorted === 'asc' ? <ChevronUp size={12} /> : sorted === 'desc' ? <ChevronDown size={12} /> : <ChevronsUpDown size={12} className="opacity-40" />}
                            </button>
                          ) : label}
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-[#e2e8f0]">
                {table.getRowModel().rows.map(row => (
                  <tr key={row.id} className="hover:bg-slate-50/60 transition-colors">
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id} className={`px-5 py-3 ${cell.column.columnDef.meta?.className ?? ''}`}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-t border-[#e2e8f0] bg-slate-50/50 text-xs text-slate-500">
            <span>Showing {first}–{last} of {total}</span>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2">
                Rows
                <select value={pagination.pageSize} onChange={(e) => setPagination({ pageIndex: 0, pageSize: Number(e.target.value) })}
                  className="border border-[#e2e8f0] rounded-lg px-2 py-1 text-xs text-navy bg-white focus:ring-2 focus:ring-primary outline-none">
                  {pageSizes.map(size => <option key={size} value={size}>{size}</option>)}
                </select>
              </label>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()} title="Previous page"
                  className="p-1.5 rounded-lg border border-[#e2e8f0] bg-white text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:pointer-events-none">
                  <ChevronLeft size={14} />
                </button>
                {pageWindow(pagination.pageIndex, pageCount).map((page, i) =>
                  page === null ? (
                    <span key={`gap-${i}`} className="px-1">…</span>
                  ) : (
                    <button key={page} type="button" onClick={() => table.setPageIndex(page)}
                      className={`min-w-[28px] h-7 px-2 rounded-lg text-xs font-semibold transition-colors ${
                        page === pagination.pageIndex ? 'bg-primary text-white' : 'border border-[#e2e8f0] bg-white text-slate-500 hover:bg-slate-50'
                      }`}>
                      {page + 1}
                    </button>
                  )
                )}
                <button type="button" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()} title="Next page"
                  className="p-1.5 rounded-lg border border-[#e2e8f0] bg-white text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:pointer-events-none">
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// First, last and two either side of the current page; null marks a gap.
function pageWindow(current: number, count: number): Array<number | null> {
  const pages: Array<number | null> = [];
  for (let i = 0; i < count; i++) {
    if (i === 0 || i === count - 1 || Math.abs(i - current) <= 2) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== null) {
      pages.push(null);
    }
  }
  return pages;
}
